import {EventNames} from "./event_names";

type MetaPixelFunction = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    loaded?: boolean;
    push?: (...args: unknown[]) => void;
    queue?: unknown[][];
    version?: string;
};

type TrackingWindow = Window & {
    Brevo?: unknown[][];
    _nQc?: string;
    brevo?: { track?: (name: string, properties?: Record<string, unknown>) => void };
    dataLayer?: unknown[][];
    fbq?: MetaPixelFunction;
    gtag?: (...args: unknown[]) => void;
};

const config = {
    albacrossClientId: process.env.REACT_APP_MARKETING_ALBACROSS_CLIENT_ID?.trim(),
    brevoClientKey: process.env.REACT_APP_MARKETING_BREVO_CLIENT_KEY?.trim(),
    googleAdsSignupConversionLabel: process.env.REACT_APP_MARKETING_GOOGLE_ADS_SIGNUP_CONVERSION_LABEL?.trim(),
    googleAdsTagId: process.env.REACT_APP_MARKETING_GOOGLE_ADS_TAG_ID?.trim() || 'AW-772205614',
    metaPixelId: process.env.REACT_APP_MARKETING_META_PIXEL_ID?.trim() || '1319740575369007',
};

let initialized = false;

function loadScript(id: string, src: string) {
    if (document.getElementById(id)) return;

    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
}

export function initMarketingTracking() {
    if (initialized) return;
    initialized = true;

    const trackingWindow = window as TrackingWindow;

    if (config.googleAdsTagId) {
        trackingWindow.dataLayer = trackingWindow.dataLayer || [];
        trackingWindow.gtag = trackingWindow.gtag || ((...args: unknown[]) => trackingWindow.dataLayer?.push(args));
        trackingWindow.gtag('js', new Date());
        trackingWindow.gtag('config', config.googleAdsTagId);
        loadScript('google-ads-tag', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.googleAdsTagId)}`);
    }

    if (config.metaPixelId) {
        if (!trackingWindow.fbq) {
            const fbq = ((...args: unknown[]) => {
                if (fbq.callMethod) fbq.callMethod(...args);
                else fbq.queue?.push(args);
            }) as MetaPixelFunction;
            fbq.queue = [];
            fbq.loaded = true;
            fbq.version = '2.0';
            fbq.push = (...args: unknown[]) => fbq(...args);
            trackingWindow.fbq = fbq;
        }
        trackingWindow.fbq('init', config.metaPixelId);
        trackingWindow.fbq('track', 'PageView');
        loadScript('meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    if (config.albacrossClientId) {
        trackingWindow._nQc = config.albacrossClientId;
        loadScript('albacross-tracker', 'https://serve.albacross.com/track.js');
    }

    if (config.brevoClientKey) {
        trackingWindow.Brevo = trackingWindow.Brevo || [];
        trackingWindow.Brevo.push(['init', {client_key: config.brevoClientKey}]);
        loadScript('brevo-tracker', 'https://cdn.brevo.com/js/sdk-loader.js');
    }
}

export function trackMarketingEvent(name: EventNames) {
    if (!initialized || name !== EventNames.SIGNUP_COMPLETED) return;

    const trackingWindow = window as TrackingWindow;

    trackingWindow.fbq?.('track', 'CompleteRegistration');

    if (config.googleAdsTagId && config.googleAdsSignupConversionLabel) {
        trackingWindow.gtag?.('event', 'conversion', {
            send_to: `${config.googleAdsTagId}/${config.googleAdsSignupConversionLabel}`,
        });
    }

    if (trackingWindow.brevo?.track) {
        trackingWindow.brevo.track('signup_completed');
    } else {
        trackingWindow.Brevo?.push(['track', 'signup_completed']);
    }
}
